import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Card, CardContent, Grid, Chip, Stack,
  CircularProgress, Alert, Divider, Avatar,
} from '@mui/material';
import { ArrowBack, Edit, Business, Phone, Email, LocationOn, WorkOutline } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { contractorService } from '@smartgrow/shared';

const fmtDate = (s?: string) => s ? new Date(s).toLocaleDateString('en-NG', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const InfoRow = ({ icon, label, value }: { icon: React.ReactNode; label: string; value?: string | null }) => (
  value ? (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1 }}>
      <Box sx={{ color: 'text.secondary', mt: 0.2, flexShrink: 0 }}>{icon}</Box>
      <Box>
        <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
        <Typography variant="body2">{value}</Typography>
      </Box>
    </Box>
  ) : null
);

export const ContractorDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const contractorId = id!;

  const { data: contractor, isLoading, isError } = useQuery({
    queryKey: ['contractor', contractorId],
    queryFn: () => contractorService.getContractorById(contractorId),
    enabled: !!contractorId,
  });

  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}><CircularProgress /></Box>;

  if (isError || !contractor) return (
    <Box sx={{ p: 3 }}>
      <Alert severity="error">Failed to load contractor.</Alert>
      <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mt: 2 }}>Back</Button>
    </Box>
  );

  const initials = contractor.companyName.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} size="small">Back</Button>
        <Box sx={{ flex: 1 }} />
        <Button startIcon={<Edit />} variant="outlined" size="small" onClick={() => navigate(`/dashboard/contractors/${contractorId}/edit`)}>
          Edit
        </Button>
      </Stack>

      {/* Header card */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Stack direction="row" spacing={2.5} alignItems="center">
            <Avatar sx={{ width: 64, height: 64, bgcolor: 'primary.main', fontSize: 22 }}>
              {initials}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" fontWeight="bold">{contractor.companyName}</Typography>
              <Typography variant="body2" color="text.secondary" fontFamily="monospace" sx={{ mb: 1 }}>
                {contractor.contractorCode}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Chip label={contractor.isActive ? 'Active' : 'Inactive'} color={contractor.isActive ? 'success' : 'error'} size="small" />
                <Chip
                  label={contractor.laborType === 'CASUAL_DAY_LABOR' ? 'Casual / Day Labor' : 'Formal Contractor'}
                  color={contractor.laborType === 'CASUAL_DAY_LABOR' ? 'warning' : 'default'}
                  size="small"
                  variant="outlined"
                />
                {contractor.specialty && <Chip label={contractor.specialty} size="small" variant="outlined" />}
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Company Details</Typography>
              <Divider sx={{ mb: 1.5 }} />
              <InfoRow icon={<Business fontSize="small" />} label="Company Name" value={contractor.companyName} />
              <InfoRow icon={<WorkOutline fontSize="small" />} label="Specialty" value={contractor.specialty} />
              <InfoRow icon={<LocationOn fontSize="small" />} label="Address" value={contractor.address} />
              {contractor.notes && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="caption" color="text.secondary">Notes</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>{contractor.notes}</Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Contact Information</Typography>
              <Divider sx={{ mb: 1.5 }} />
              <InfoRow icon={<Business fontSize="small" />} label="Contact Person" value={contractor.contactPerson} />
              <InfoRow icon={<Phone fontSize="small" />} label="Phone" value={contractor.phone} />
              <InfoRow icon={<Email fontSize="small" />} label="Email" value={contractor.email} />
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="caption" color="text.secondary">Registered Since</Typography>
              <Typography variant="body2">{fmtDate(contractor.createdAt)}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
